import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (error) {
        console.error('Failed to parse cookie consent:', error);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    
    // Initialize analytics and marketing scripts here
    initializeOptionalCookies(allAccepted);
  };

  const handleRejectAll = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    localStorage.setItem('cookie_consent', JSON.stringify(essentialOnly));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify(preferences));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    
    // Initialize only enabled cookies
    initializeOptionalCookies(preferences);
  };

  const initializeOptionalCookies = (prefs: CookiePreferences) => {
    // Initialize analytics if enabled
    if (prefs.analytics) {
      // Example: Initialize Google Analytics
      // window.gtag('consent', 'update', { analytics_storage: 'granted' });
      console.log('Analytics cookies enabled');
    }

    // Initialize marketing if enabled
    if (prefs.marketing) {
      // Example: Initialize marketing pixels
      // window.fbq('consent', 'grant');
      console.log('Marketing cookies enabled');
    }

    // Functional cookies
    if (prefs.functional) {
      console.log('Functional cookies enabled');
    }
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return; // Cannot disable essential cookies
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 pb-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-orange-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
            </div>
            <button
              onClick={handleRejectAll}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close banner"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">
              We use cookies to enhance your experience, analyze site traffic, and personalize content. 
              You can customize your preferences or accept all cookies.
            </p>
            <Link to="/legal/cookie-policy" className="text-sm text-blue-600 hover:text-blue-700 hover:underline">
              Learn more about our cookies
            </Link>
          </div>

          {/* Details Section */}
          {showDetails && (
            <div className="mb-6 space-y-4">
              {/* Essential Cookies */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={preferences.essential}
                    disabled
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 opacity-50 cursor-not-allowed"
                  />
                </div>
                <div className="ml-3">
                  <label className="text-sm font-medium text-gray-900">
                    Essential Cookies <span className="text-gray-500">(Required)</span>
                  </label>
                  <p className="text-xs text-gray-600">
                    Necessary for the platform to function. These cannot be disabled.
                  </p>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={() => togglePreference('functional')}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                </div>
                <div className="ml-3">
                  <label className="text-sm font-medium text-gray-900">Functional Cookies</label>
                  <p className="text-xs text-gray-600">
                    Remember your preferences like language and theme settings.
                  </p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => togglePreference('analytics')}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                </div>
                <div className="ml-3">
                  <label className="text-sm font-medium text-gray-900">Analytics Cookies</label>
                  <p className="text-xs text-gray-600">
                    Help us understand how you use the platform to improve performance.
                  </p>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => togglePreference('marketing')}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                </div>
                <div className="ml-3">
                  <label className="text-sm font-medium text-gray-900">Marketing Cookies</label>
                  <p className="text-xs text-gray-600">
                    Used to show you relevant advertisements and measure campaign effectiveness.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {!showDetails ? (
              <>
                <Button 
                  onClick={handleAcceptAll} 
                  className="flex-1"
                >
                  Accept All Cookies
                </Button>
                <Button 
                  onClick={() => setShowDetails(true)} 
                  variant="secondary"
                  className="flex-1"
                >
                  Customize Preferences
                </Button>
                <Button 
                  onClick={handleRejectAll} 
                  variant="outline"
                  className="flex-1"
                >
                  Reject All
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={handleSavePreferences} 
                  className="flex-1"
                >
                  Save My Preferences
                </Button>
                <Button 
                  onClick={handleAcceptAll} 
                  variant="secondary"
                  className="flex-1"
                >
                  Accept All
                </Button>
                <Button 
                  onClick={() => setShowDetails(false)} 
                  variant="outline"
                  className="flex-1"
                >
                  Back
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
